import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { HomeIcon, PlusIcon, EditIcon, TrashIcon, CopyIcon, ToggleLeftIcon, ToggleRightIcon, EyeIcon, EyeOffIcon, ClipboardIcon, ImageIcon, XIcon, Type, Star, MessageSquare, Quote, LayoutGrid, FormInput, CircleDot, Hexagon, AlertTriangle, CheckCircle2, Circle, Globe, Smartphone } from 'lucide-react';
import { tenantApi, type WidgetHomepageConfig, type MultipleHomepageCreateRequest, type MultipleHomepageUpdateRequest, type Asset, type CustomFormField } from '../lib/tenant-api';
import { toast } from 'react-toastify';
import { PageLoader } from './common';
import { ErrorBanner, PageHeader } from './expert/ui';
import { useTenantAuth } from '../contexts/TenantAuthContext';
import { useReadOnly } from '../contexts/ReadOnlyContext';
import { EXPERT_ROUTES, isDomainExpertEnabled } from '../lib/workspace';
import { expertAgentApi, expertKeys, getExpertErrorCode, getExpertErrorMessage, isRequestCancelled } from '../lib/domain-expert-api';
import RichTextEditor from './rich-text-editor/RichTextEditor';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableSection } from './homepage/SortableSection';
import { useHomepageSections } from './homepage/HomepageSections';
import { DEFAULT_FORM_FIELDS, DEFAULT_SECTION_ORDER, VISITOR_ONLY_SECTIONS } from './homepage/types';
import type { CustomFormFieldItem, HomepageManagementProps } from './homepage/types';

type SectionChip = { label: string; Icon: React.ComponentType<{ className?: string }> };

// Same key as Settings > Widget, so both always show the same list.
const HOMEPAGES_KEY = ['widgetHomepages'];

// What the Domain Expert app shows. The expert editor sends only these, and only when changed,
// so a homepage's website-only fields (weight, is_active, show_homepage_content, form, CTA)
// are never sent from it and stay exactly as they are.
const EXPERT_FIELDS = [
  'homepage_name', 'header_title', 'header_subtitle', 'rating_stars', 'rating_text', 'message_text',
  'testimonial_quote', 'testimonial_author', 'selected_asset_ids', 'gallery_title', 'logo_asset_id',
] as const;

type HomepageForm = MultipleHomepageCreateRequest & { imageSourceType?: string; imageAssetId?: string; imageUrl?: string };

// In the website widget's weighted rotation.
const isOnWebsite = (hp: WidgetHomepageConfig) => hp.is_active && hp.show_homepage_content;

// '', null and undefined all mean "not set".
const sameValue = (a: unknown, b: unknown) => {
  const norm = (v: unknown) => (v === undefined || v === null || v === '' ? null : v);
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
};

function buildExpertUpdate(form: HomepageForm, saved: WidgetHomepageConfig, sectionOrder: string[], loadedSectionOrder: string[]): MultipleHomepageUpdateRequest {
  const update: Record<string, unknown> = {};
  for (const key of EXPERT_FIELDS) {
    if (!sameValue(form[key], saved[key])) update[key] = form[key];
  }
  // Only a reorder by the admin sends the order; the form and CTA keep their places in it.
  if (JSON.stringify(sectionOrder) !== JSON.stringify(loadedSectionOrder)) update.section_order = sectionOrder;
  return update as MultipleHomepageUpdateRequest;
}

function buildExpertCreate(form: HomepageForm, sectionOrder: string[]): MultipleHomepageCreateRequest {
  const create: Record<string, unknown> = {};
  for (const key of EXPERT_FIELDS) create[key] = form[key];
  // Content off keeps a new homepage out of the website's rotation; the expert app doesn't use the flag.
  return { ...create, show_homepage_content: false, section_order: sectionOrder } as MultipleHomepageCreateRequest;
}

// The expert app's render leaves out forms and CTAs, so its preview does too.
const withoutFormAndCta = <T extends object>(data: T) => ({ ...data, selected_cta_id: null, selected_form_cta_id: null, custom_form_config: null });

function Spinner({ small = false }: { small?: boolean }) {
  return <span className={`${small ? 'h-3.5 w-3.5' : 'h-5 w-5'} flex-shrink-0 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-600`} />;
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />
      <span>{children}</span>
    </div>
  );
}

// The expert editor keeps the form and CTA in the order (they belong to the website) but doesn't edit them.
function WebsiteOnlySection({ sectionId }: { sectionId: string }) {
  const Icon = sectionId === 'form' ? FormInput : CircleDot;
  return (
    <div className="bg-gray-50 rounded-xl border border-dashed border-gray-200 p-5">
      <div className="pl-8 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4 text-gray-400" />
          </div>
          <h4 className="text-sm font-semibold text-gray-500">{sectionId === 'form' ? 'Form' : 'Call-to-Action'}</h4>
        </div>
        <span className="text-xs text-gray-500">Not shown in the Domain Expert app</span>
      </div>
    </div>
  );
}

// Derive the section chips shown on a card: one chip per populated section,
// ordered by the config's saved section_order (falls back to the default order).
function buildChips(hp: WidgetHomepageConfig, skipVisitorOnly = false): SectionChip[] {
  const order = hp.section_order && hp.section_order.length ? hp.section_order : DEFAULT_SECTION_ORDER;
  const chips: SectionChip[] = [];
  for (const section of order) {
    if (skipVisitorOnly && VISITOR_ONLY_SECTIONS.includes(section)) continue;
    switch (section) {
      case 'logo':
        if (hp.logo_asset_id) chips.push({ label: 'Logo', Icon: Hexagon });
        break;
      case 'header':
        if (hp.header_title) chips.push({ label: 'Header', Icon: Type });
        if (hp.header_subtitle) chips.push({ label: 'Header Subtitle', Icon: Type });
        break;
      case 'rating':
        if (hp.rating_stars) chips.push({ label: `Rating ${hp.rating_stars.toFixed(1)}`, Icon: Star });
        break;
      case 'message':
        if (hp.message_text) chips.push({ label: 'Messaging', Icon: MessageSquare });
        break;
      case 'testimonial':
        if (hp.testimonial_quote) chips.push({ label: 'Testimonial', Icon: Quote });
        break;
      case 'gallery':
        if (hp.selected_asset_ids?.length) chips.push({ label: `Gallery ${hp.selected_asset_ids.length}`, Icon: LayoutGrid });
        break;
      case 'form':
        if (hp.selected_form_cta_id || hp.custom_form_config) chips.push({ label: 'Form', Icon: FormInput });
        break;
      case 'cta':
        if (hp.selected_cta_id) chips.push({ label: 'CTA', Icon: CircleDot });
        break;
    }
  }
  return chips;
}

export default function HomepageManagement({ assets: initialAssets, ctas, mode = 'visitor' }: HomepageManagementProps) {
  const isExpert = mode === 'expert';
  const { tenant } = useTenantAuth();
  const { isReadOnly } = useReadOnly();
  const queryClient = useQueryClient();
  // The expert workspace can't change anything for read-only roles; the visitor editor relies on the write blocker as before.
  const expertReadOnly = isExpert && isReadOnly;

  // Fresh on every visit (and, in the expert workspace, on every return to the tab).
  const homepagesQuery = useQuery<WidgetHomepageConfig[]>({
    queryKey: HOMEPAGES_KEY,
    queryFn: tenantApi.getHomepages,
    refetchOnWindowFocus: isExpert,
    onError: (error) => {
      console.error('Failed to load homepages:', error);
      toast.error('Failed to load homepages', { toastId: 'homepages-load-failed' });
    },
  });
  const homepages = homepagesQuery.data ?? [];
  const loading = homepagesQuery.isInitialLoading;
  const setHomepages = (update: (current: WidgetHomepageConfig[]) => WidgetHomepageConfig[]) => {
    queryClient.setQueryData<WidgetHomepageConfig[]>(HOMEPAGES_KEY, (current) => update(current ?? []));
    queryClient.invalidateQueries({ queryKey: HOMEPAGES_KEY });
  };

  // The one homepage the Domain Expert app shows (null = welcome message), kept by ai-api.
  const expertEnabled = isExpert || isDomainExpertEnabled(tenant);
  const agentSettingsQuery = useQuery({
    queryKey: expertKeys.agentSettings(),
    queryFn: expertAgentApi.getSettings,
    enabled: expertEnabled,
    refetchOnWindowFocus: isExpert,
    retry: isExpert ? 1 : false,
  });
  const expertHomepageId = agentSettingsQuery.data?.homepage_id ?? null;
  const expertChoiceKnown = expertEnabled && agentSettingsQuery.isSuccess;
  // undefined = idle; otherwise the homepage id being made active (null = none).
  const [activating, setActivating] = useState<string | null | undefined>(undefined);

  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingHomepage, setEditingHomepage] = useState<WidgetHomepageConfig | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingHomepage, setDeletingHomepage] = useState<WidgetHomepageConfig | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  // Standalone card "Preview" modal (independent of the in-editor preview above)
  const [previewModalHomepage, setPreviewModalHomepage] = useState<WidgetHomepageConfig | null>(null);
  const [previewModalContent, setPreviewModalContent] = useState<string>('');
  const [previewModalLoading, setPreviewModalLoading] = useState(false);
  const [assets, setAssets] = useState<Asset[]>(initialAssets);
  // The order as loaded into the editor, so the expert editor sends an order only after a reorder.
  const [loadedSectionOrder, setLoadedSectionOrder] = useState<string[]>([...DEFAULT_SECTION_ORDER]);

  const [homepageForm, setHomepageForm] = useState<HomepageForm>({
    homepage_name: '',
    weight: 100,
    show_homepage_content: false,
    header_title: '',
    header_subtitle: '',
    rating_stars: null,
    rating_text: '',
    message_text: '',
    testimonial_quote: '',
    testimonial_author: '',
    selected_asset_ids: [],
    selected_cta_id: null,
    selected_form_cta_id: null,
    custom_form_config: null,
    gallery_title: '',
    logo_asset_id: null,
    section_order: null,
    imageSourceType: '',
    imageAssetId: '',
    imageUrl: ''
  });

  const [customFormFields, setCustomFormFields] = useState<CustomFormFieldItem[]>([...DEFAULT_FORM_FIELDS]);
  const [customFormTitle, setCustomFormTitle] = useState('');
  const [customFormSubmitText, setCustomFormSubmitText] = useState('Send Message');
  const [customFormSubmitType, setCustomFormSubmitType] = useState<'submit' | 'call_submit'>('submit');
  const [customFormPhoneNumber, setCustomFormPhoneNumber] = useState('');
  const [sectionOrder, setSectionOrder] = useState<string[]>([...DEFAULT_SECTION_ORDER]);

  // Use extracted section renderers
  const { renderSection } = useHomepageSections({
    homepageForm, setHomepageForm,
    assets, setAssets,
    ctas,
    customFormFields, setCustomFormFields,
    customFormTitle, setCustomFormTitle,
    customFormSubmitText, setCustomFormSubmitText,
    customFormSubmitType, setCustomFormSubmitType,
    customFormPhoneNumber, setCustomFormPhoneNumber,
  });

  // Set up sensors for drag-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sync custom form fields to homepage form config
  useEffect(() => {
    const hasAnyFieldEnabled = customFormFields.some(f => f.enabled);

    if (hasAnyFieldEnabled) {
      const fields: CustomFormField[] = customFormFields
        .filter(f => f.enabled)
        .map(f => ({
          type: f.type,
          id: f.id,
          label: f.label,
          placeholder: f.placeholder,
          required: true
        }));

      setHomepageForm(prev => ({
        ...prev,
        custom_form_config: {
          title: customFormTitle || undefined,
          submit_text: customFormSubmitText,
          type: customFormSubmitType,
          value: customFormSubmitType === 'call_submit' ? customFormPhoneNumber : '',
          fields
        },
        selected_form_cta_id: null
      }));
    } else {
      setHomepageForm(prev => ({
        ...prev,
        custom_form_config: null
      }));
    }
  }, [customFormFields, customFormTitle, customFormSubmitText, customFormSubmitType, customFormPhoneNumber]);

  // Sync section order to homepage form
  useEffect(() => {
    setHomepageForm(prev => ({ ...prev, section_order: sectionOrder }));
  }, [sectionOrder]);

  // Handle drag end for sections
  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSectionOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHomepageForm(prev => ({ ...prev, section_order: newOrder }));
        return newOrder;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);

      if (editingHomepage) {
        const updateData: MultipleHomepageUpdateRequest = isExpert
          ? buildExpertUpdate(homepageForm, editingHomepage, sectionOrder, loadedSectionOrder)
          : { ...homepageForm };
        if (isExpert && Object.keys(updateData).length === 0) {
          toast.info('No changes to save.');
          setShowModal(false);
          resetForm();
          return;
        }
        const response = await tenantApi.updateHomepage(editingHomepage.id, updateData);
        setHomepages(current => current.map(hp => hp.id === editingHomepage.id ? response : hp));
        toast.success('Homepage updated successfully!');
        window.dispatchEvent(new Event('widget-homepage-content-changed'));
      } else {
        const response = await tenantApi.createHomepage(isExpert ? buildExpertCreate(homepageForm, sectionOrder) : homepageForm);
        setHomepages(current => [response, ...current]);
        toast.success(isExpert ? 'Homepage created. Set it active to show it in the Domain Expert app.' : 'Homepage created successfully!');
        window.dispatchEvent(new Event('widget-homepage-content-changed'));
      }

      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save homepage:', error);
      toast.error('Failed to save homepage');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (homepageId: string) => {
    try {
      let response = await tenantApi.duplicateHomepage(homepageId);
      // A copy made for the expert app must not join the website's rotation (the server copies is_active).
      if (isExpert && isOnWebsite(response)) {
        try {
          response = await tenantApi.updateHomepage(response.id, { is_active: false });
        } catch (error) {
          console.error('Failed to take the copy out of the website rotation:', error);
          toast.warn('The copy is also in your website\'s homepage rotation. Turn it off in Client Admin > Home Page Configurations.');
        }
      }
      const copy = response;
      setHomepages(current => [copy, ...current]);
      toast.success('Homepage duplicated successfully!');
    } catch (error) {
      console.error('Failed to duplicate homepage:', error);
      toast.error('Failed to duplicate homepage');
    }
  };

  // Expert app: exactly one homepage or none. Saved by ai-api in the expert agent settings.
  const setExpertHomepage = async (homepageId: string | null) => {
    if (expertReadOnly || !expertChoiceKnown || activating !== undefined || homepageId === expertHomepageId) return;
    setActivating(homepageId);
    try {
      const settings = await expertAgentApi.updateSettings({ homepage_id: homepageId });
      // A reload that was already running read the old choice; drop it so it can't overwrite this one.
      await queryClient.cancelQueries({ queryKey: expertKeys.agentSettings() });
      queryClient.setQueryData(expertKeys.agentSettings(), settings);
      if ((settings.homepage_id ?? null) !== homepageId) {
        // An ai-api without the expert homepage drops the field.
        toast.error("The Domain Expert app's home screen couldn't be changed yet. Please try again later.");
      } else {
        toast.success(homepageId ? 'The Domain Expert app now shows this homepage.' : 'The Domain Expert app now shows the welcome message.');
      }
    } catch (error) {
      if (isRequestCancelled(error)) return;
      console.error('Failed to set the expert app homepage:', error);
      if (getExpertErrorCode(error) === 'homepage_not_found') {
        toast.error("That homepage isn't available any more. The list has been refreshed.");
        queryClient.invalidateQueries({ queryKey: HOMEPAGES_KEY });
        queryClient.invalidateQueries({ queryKey: expertKeys.agentSettings() });
      } else {
        toast.error(getExpertErrorMessage(error, "We couldn't change the Domain Expert app's home screen. Please try again."));
      }
    } finally {
      setActivating(undefined);
    }
  };

  const handleToggleActive = async (homepage: WidgetHomepageConfig) => {
    try {
      const updateData: MultipleHomepageUpdateRequest = { is_active: !homepage.is_active };
      const response = await tenantApi.updateHomepage(homepage.id, updateData);
      setHomepages(current => current.map(hp => hp.id === homepage.id ? response : hp));
      toast.success(`Homepage ${response.is_active ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Failed to toggle homepage status:', error);
      toast.error('Failed to update homepage status');
    }
  };

  const confirmDelete = (homepage: WidgetHomepageConfig) => {
    setDeletingHomepage(homepage);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingHomepage) return;
    try {
      await tenantApi.deleteHomepage(deletingHomepage.id);
      const deletedId = deletingHomepage.id;
      setHomepages(current => current.filter(hp => hp.id !== deletedId));
      // ai-api clears the expert app's choice when its homepage is deleted.
      if (expertEnabled && deletedId === expertHomepageId) {
        queryClient.invalidateQueries({ queryKey: expertKeys.agentSettings() });
      }
      toast.success('Homepage deleted successfully!');
    } catch (error) {
      console.error('Failed to delete homepage:', error);
      toast.error('Failed to delete homepage');
    } finally {
      setShowDeleteConfirm(false);
      setDeletingHomepage(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeletingHomepage(null);
  };

  const openModal = (homepage?: WidgetHomepageConfig) => {
    if (homepage) {
      setEditingHomepage(homepage);

      let loadedSectionOrder = homepage.section_order || [...DEFAULT_SECTION_ORDER];
      // The expert editor leaves the stored order as it is (adding 'cta' would change the website's homepage).
      if (!isExpert && !loadedSectionOrder.includes('cta')) {
        const formIndex = loadedSectionOrder.indexOf('form');
        if (formIndex !== -1) {
          loadedSectionOrder = [...loadedSectionOrder.slice(0, formIndex + 1), 'cta', ...loadedSectionOrder.slice(formIndex + 1)];
        } else {
          loadedSectionOrder = [...loadedSectionOrder, 'cta'];
        }
      }

      setSectionOrder(loadedSectionOrder);
      setLoadedSectionOrder(loadedSectionOrder);

      setHomepageForm({
        homepage_name: homepage.homepage_name,
        weight: homepage.weight,
        show_homepage_content: homepage.show_homepage_content,
        header_title: homepage.header_title || '',
        header_subtitle: homepage.header_subtitle || '',
        rating_stars: homepage.rating_stars,
        rating_text: homepage.rating_text || '',
        message_text: homepage.message_text || '',
        testimonial_quote: homepage.testimonial_quote || '',
        testimonial_author: homepage.testimonial_author || '',
        selected_asset_ids: homepage.selected_asset_ids || [],
        selected_cta_id: homepage.selected_cta_id || undefined,
        selected_form_cta_id: homepage.selected_form_cta_id || undefined,
        custom_form_config: homepage.custom_form_config || null,
        gallery_title: homepage.gallery_title || '',
        logo_asset_id: homepage.logo_asset_id || null,
        section_order: loadedSectionOrder
      });

      if (homepage.custom_form_config?.fields) {
        const defaultFields = [...DEFAULT_FORM_FIELDS];
        const orderedFields: CustomFormFieldItem[] = [];
        const remainingFields = [...defaultFields];

        for (const savedField of homepage.custom_form_config.fields) {
          const fieldDef = remainingFields.find(f => f.id === savedField.id);
          if (fieldDef) {
            orderedFields.push({ ...fieldDef, enabled: true });
            const index = remainingFields.findIndex(f => f.id === savedField.id);
            remainingFields.splice(index, 1);
          }
        }
        orderedFields.push(...remainingFields);

        setCustomFormFields(orderedFields);
        setCustomFormTitle(homepage.custom_form_config.title || '');
        setCustomFormSubmitText(homepage.custom_form_config.submit_text || 'Send Message');
        setCustomFormSubmitType(homepage.custom_form_config.type || 'submit');
        setCustomFormPhoneNumber(homepage.custom_form_config.value || '');
      } else {
        setCustomFormFields([...DEFAULT_FORM_FIELDS]);
        setCustomFormTitle('');
        setCustomFormSubmitText('Send Message');
        setCustomFormSubmitType('submit');
        setCustomFormPhoneNumber('');
      }
    } else {
      setEditingHomepage(null);
      resetForm();
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setSectionOrder([...DEFAULT_SECTION_ORDER]);
    setLoadedSectionOrder([...DEFAULT_SECTION_ORDER]);
    setHomepageForm({
      homepage_name: '', weight: 100, show_homepage_content: false,
      header_title: '', header_subtitle: '', rating_stars: null, rating_text: '',
      message_text: '', testimonial_quote: '', testimonial_author: '',
      selected_asset_ids: [], selected_cta_id: null, selected_form_cta_id: null,
      custom_form_config: null, gallery_title: '', logo_asset_id: null,
      section_order: null, imageSourceType: '', imageAssetId: '', imageUrl: ''
    });
    setCustomFormFields([...DEFAULT_FORM_FIELDS]);
    setCustomFormTitle('');
    setCustomFormSubmitText('Send Message');
    setCustomFormSubmitType('submit');
    setCustomFormPhoneNumber('');
  };

  const handlePreview = async () => {
    if (!showPreview) {
      try {
        setPreviewLoading(true);
        const previewData = { ...homepageForm, id: editingHomepage?.id || undefined };
        const response = await tenantApi.previewHomepage(isExpert ? withoutFormAndCta(previewData) : previewData);
        setPreviewContent(response.preview_content);
        setShowPreview(true);
      } catch (error) {
        console.error('Failed to generate preview:', error);
        toast.error('Failed to generate preview');
      } finally {
        setPreviewLoading(false);
      }
    } else {
      setShowPreview(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(previewContent);
    toast.success('Copied to clipboard!');
  };

  // Card "Preview" — open a dedicated read-only modal and render the server preview.
  const handleCardPreview = async (homepage: WidgetHomepageConfig) => {
    setPreviewModalHomepage(homepage);
    setPreviewModalContent('');
    setPreviewModalLoading(true);
    try {
      const response = await tenantApi.previewHomepage(isExpert ? withoutFormAndCta(homepage) : { ...homepage });
      setPreviewModalContent(response.preview_content);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      toast.error('Failed to generate preview');
      setPreviewModalHomepage(null);
    } finally {
      setPreviewModalLoading(false);
    }
  };

  const copyPreviewModalContent = () => {
    navigator.clipboard.writeText(previewModalContent);
    toast.success('Copied to clipboard!');
  };

  const calculateWeightPercentage = (weight: number, totalWeight: number) => {
    if (totalWeight === 0) return 0;
    return Math.round((weight / totalWeight) * 100);
  };

  const activeHomepages = homepages.filter(hp => hp.is_active && hp.show_homepage_content);
  const totalActiveWeight = activeHomepages.reduce((sum, hp) => sum + hp.weight, 0);
  const editingIsExpertHomepage = !!editingHomepage && expertChoiceKnown && editingHomepage.id === expertHomepageId;
  const deletingIsExpertHomepage = !!deletingHomepage && expertChoiceKnown && deletingHomepage.id === expertHomepageId;
  const expertChoiceDisabled = expertReadOnly || !expertChoiceKnown || activating !== undefined;
  const noneIsActive = expertChoiceKnown && expertHomepageId === null;

  if (loading) {
    return <PageLoader />;
  }

  const createButton = (
    <button
      onClick={() => openModal()}
      disabled={expertReadOnly}
      className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-indigo-500 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <PlusIcon className="h-4 w-4 mr-2" />
      Create Homepage
    </button>
  );

  return (
    <div>
      <div className="space-y-6">
        {isExpert ? (
          <>
          <Link to={EXPERT_ROUTES.settings} className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800">
            ← Settings
          </Link>
          <PageHeader
            title="Home screen"
            description="What users see at the top of a new chat in the Domain Expert app, above your suggested questions. Forms and CTAs are never shown there."
            actions={createButton}
          />
          </>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Home Page Configurations</h1>
              <p className="mt-1 text-sm text-gray-600">
                Create and manage multiple homepage variations with weighted randomization
              </p>
            </div>
            {createButton}
          </div>
        )}

        {isExpert && agentSettingsQuery.isError && (
          <ErrorBanner message={getExpertErrorMessage(agentSettingsQuery.error, "We couldn't load which homepage the Domain Expert app shows. Refresh the page to try again.")} />
        )}

        <div
          className={isExpert ? 'space-y-4' : undefined}
          role={isExpert ? 'radiogroup' : undefined}
          aria-label={isExpert ? 'Home screen in the Domain Expert app' : undefined}
        >
          {isExpert && (
            <button
              type="button"
              role="radio"
              aria-checked={noneIsActive}
              disabled={expertChoiceDisabled}
              onClick={() => setExpertHomepage(null)}
              className={`w-full flex items-center gap-3 rounded-lg border p-4 text-left transition-colors disabled:cursor-not-allowed ${noneIsActive ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
            >
              {activating === null ? <Spinner /> : noneIsActive ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" /> : <Circle className="h-5 w-5 flex-shrink-0 text-gray-400" />}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">None — show the welcome message and suggested questions</span>
                <span className="block text-xs text-gray-500">The welcome message and suggested questions are set in Settings.</span>
              </span>
              {noneIsActive && <span className="flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>}
            </button>
          )}

          {homepages.length === 0 ? (
            <div className="text-center py-8">
              <HomeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No homepage configurations</h3>
              <p className="mt-1 text-sm text-gray-500">
                {isExpert
                  ? 'Create a homepage to show it at the top of a new chat in the Domain Expert app.'
                  : 'Create your first homepage to get started with multiple homepage support.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {!isExpert && activeHomepages.length > 1 && (
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-50 border border-indigo-200  p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-semibold text-indigo-900">Active Homepage Distribution</h4>
                    <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">{activeHomepages.length} active</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {activeHomepages.map((homepage) => (
                      <div key={homepage.id} className="bg-white/60 backdrop-blur-sm  p-3 border border-indigo-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-indigo-800 truncate pr-2">{homepage.homepage_name}</span>
                          <span className="text-xs font-bold text-indigo-900 bg-indigo-200 px-2 py-1 ">{calculateWeightPercentage(homepage.weight, totalActiveWeight)}%</span>
                        </div>
                        <div className="w-full bg-indigo-200 rounded-full h-1.5">
                          <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300" style={{ width: `${calculateWeightPercentage(homepage.weight, totalActiveWeight)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {homepages.map((homepage) => {
                  const chips = buildChips(homepage, isExpert);
                  const thumbs = homepage.selected_asset_ids
                    .map((id) => assets.find((a) => a.id === id))
                    .filter((a): a is Asset => !!a && a.asset_type === 'image')
                    .slice(0, 3);
                  const extraCount = homepage.selected_asset_ids.length - thumbs.length;
                  const pct = calculateWeightPercentage(homepage.weight, totalActiveWeight);
                  const isExpertHomepage = expertChoiceKnown && homepage.id === expertHomepageId;

                  return (
                    <div key={homepage.id} className={`flex flex-col bg-white border ${isExpert && isExpertHomepage ? 'border-green-300 ring-1 ring-green-200' : 'border-gray-200'} rounded-lg p-5 hover:shadow-md transition-shadow`}>
                      {/* Top row: asset thumbnails + active pill */}
                      <div className="flex items-start justify-between mb-4">
                        {thumbs.length > 0 ? (
                          <div className="flex items-center -space-x-2">
                            {thumbs.map((a) => (
                              <img
                                key={a.id}
                                src={a.custom_metadata.public_url}
                                alt={a.file_name}
                                className="h-9 w-9 rounded-lg object-cover border-2 border-white shadow-sm"
                              />
                            ))}
                            {extraCount > 0 && (
                              <span className="h-9 w-9 rounded-lg border-2 border-white bg-gray-100 text-gray-600 text-xs font-medium flex items-center justify-center shadow-sm">
                                +{extraCount}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="h-9 w-9 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400">
                            <ImageIcon className="h-4 w-4" />
                          </div>
                        )}

                        {isExpert ? (
                          <button
                            type="button"
                            role="radio"
                            aria-checked={isExpertHomepage}
                            aria-label={`${homepage.homepage_name}: ${isExpertHomepage ? 'Active' : 'Set active'}`}
                            disabled={expertChoiceDisabled}
                            onClick={() => setExpertHomepage(homepage.id)}
                            title={isExpertHomepage ? 'Shown in the Domain Expert app' : 'Show this homepage in the Domain Expert app'}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:cursor-not-allowed ${isExpertHomepage ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:hover:bg-gray-100'}`}
                          >
                            {activating === homepage.id ? <Spinner small /> : isExpertHomepage ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                            {isExpertHomepage ? 'Active' : 'Set active'}
                          </button>
                        ) : (
                          <div className="flex items-center gap-2">
                            {isExpertHomepage && (
                              <span title="The Domain Expert app shows this homepage" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                                <Smartphone className="h-3.5 w-3.5" />
                                Expert app
                              </span>
                            )}
                            <button
                              onClick={() => handleToggleActive(homepage)}
                              title={homepage.is_active ? 'Deactivate' : 'Activate'}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${homepage.is_active ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                            >
                              {homepage.is_active ? <ToggleRightIcon className="h-3.5 w-3.5" /> : <ToggleLeftIcon className="h-3.5 w-3.5" />}
                              {homepage.is_active ? 'Active' : 'Inactive'}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Name + title + subtitle */}
                      <div className="flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 truncate">{homepage.homepage_name}</p>
                        <h3 className="mt-1 text-xl font-bold text-gray-900">{homepage.header_title || homepage.homepage_name}</h3>
                        {homepage.header_subtitle && (
                          <p className="mt-1 text-sm text-gray-500">{homepage.header_subtitle}</p>
                        )}

                        {/* Section chips */}
                        {chips.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {chips.map((chip, i) => (
                              <span key={`${chip.label}-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-xs text-gray-700">
                                <chip.Icon className="h-3 w-3 text-gray-500" />
                                {chip.label}
                              </span>
                            ))}
                          </div>
                        )}
                        {!isExpert && !homepage.show_homepage_content && (
                          <p className="mt-3 text-xs italic text-gray-400">Content disabled</p>
                        )}
                        {isExpert && isOnWebsite(homepage) && (
                          <p className="mt-3 inline-flex items-center gap-1 text-xs text-gray-500">
                            <Globe className="h-3.5 w-3.5" />
                            Also shown on your website
                          </p>
                        )}

                        {/* Meta */}
                        <p className="mt-4 text-xs text-gray-500">
                          {!isExpert && (
                            <>
                              Weight: {homepage.weight}
                              {homepage.is_active && homepage.show_homepage_content && totalActiveWeight > 0 && ` (${pct}%)`}
                              {'  •  '}
                            </>
                          )}
                          {'Last modified: '}
                          {new Date(homepage.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>

                      {/* Footer */}
                      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <button
                          onClick={() => handleCardPreview(homepage)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                        >
                          <EyeIcon className="h-4 w-4" /> Preview
                        </button>
                        <div className="flex items-center gap-2">
                          <button onClick={() => openModal(homepage)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors" title="Edit"><EditIcon className="h-4 w-4" /></button>
                          <button onClick={() => handleDuplicate(homepage.id)} disabled={expertReadOnly} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Duplicate"><CopyIcon className="h-4 w-4" /></button>
                          <button onClick={() => confirmDelete(homepage)} disabled={expertReadOnly} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Delete"><TrashIcon className="h-4 w-4" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Homepage Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-start justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="w-full max-w-2xl bg-white relative my-4 sm:my-16 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <HomeIcon className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{editingHomepage ? 'Edit Homepage' : 'Create New Homepage'}</h3>
                  <p className="text-sm text-gray-500">{isExpert ? 'Configure what users see at the top of a new chat.' : 'Configure how this variation appears in the widget.'}</p>
                </div>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Close">
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
              <form onSubmit={handleSubmit} className="space-y-6">
                {isExpert && editingHomepage && isOnWebsite(editingHomepage) && (
                  <Notice>This homepage is also shown on your website. Changes you make here change it there too.</Notice>
                )}
                {!isExpert && editingIsExpertHomepage && editingHomepage && (
                  <Notice>
                    The Domain Expert app shows this homepage (without its form and CTA), so changes you make here appear there too.
                    {editingHomepage.show_homepage_content && !homepageForm.show_homepage_content && ' Turning content off only removes it from your website; users still see it.'}
                  </Notice>
                )}
                <div className={isExpert ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Homepage Name <span className="text-red-500">*</span></label>
                    <input type="text" required value={homepageForm.homepage_name} onChange={(e) => setHomepageForm({ ...homepageForm, homepage_name: e.target.value })} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="e.g., Black Friday Promo, Default Welcome" />
                  </div>
                  {!isExpert && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Weight (1-1000) <span className="text-red-500">*</span></label>
                    <input type="number" required min="1" max="1000" value={homepageForm.weight || ''} onChange={(e) => setHomepageForm({ ...homepageForm, weight: e.target.value ? parseInt(e.target.value) : undefined })} className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition" />
                    <p className="mt-1.5 text-xs text-gray-500">Higher weight = higher probability of being shown (default: 100)</p>
                  </div>
                  )}
                </div>

                {!isExpert && (
                <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50/70">
                  <div>
                    <span className="block text-sm font-medium text-gray-900">Enable custom homepage content</span>
                    <span className="block text-xs text-gray-500 mt-0.5">Design the widget's homepage with the sections below.</span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={homepageForm.show_homepage_content}
                    onClick={() => setHomepageForm({ ...homepageForm, show_homepage_content: !homepageForm.show_homepage_content })}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-indigo-500 ${homepageForm.show_homepage_content ? 'bg-indigo-600' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-5 w-5 mt-0.5 transform rounded-full bg-white shadow transition-transform ${homepageForm.show_homepage_content ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                )}

                {(isExpert || homepageForm.show_homepage_content) && (
                  <div className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-gray-900">Homepage Sections</h4>
                      <span className="text-xs text-gray-400">Drag to reorder</span>
                    </div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                      <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
                        <div className="space-y-4">
                          {sectionOrder.map((sectionId) => (
                            <SortableSection key={sectionId} section={sectionId}>
                              {isExpert && VISITOR_ONLY_SECTIONS.includes(sectionId) ? <WebsiteOnlySection sectionId={sectionId} /> : renderSection(sectionId)}
                            </SortableSection>
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}
              </form>
            </div>

            {(isExpert || homepageForm.show_homepage_content) && (
              <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-gray-100 bg-indigo-50/40">
                <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Preview Generated Markdown</h4>
                    {isExpert && <p className="text-xs text-gray-500">Forms and CTAs are not shown in the Domain Expert app.</p>}
                  </div>
                  <div className="flex space-x-2">
                    {showPreview && previewContent && (
                      <button type="button" onClick={copyToClipboard} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        <ClipboardIcon className="h-3 w-3 mr-1" />Copy
                      </button>
                    )}
                    <button type="button" onClick={handlePreview} disabled={previewLoading} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50">
                      {previewLoading ? (<><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-600 mr-1"></div>Loading...</>) : showPreview ? (<><EyeOffIcon className="h-3 w-3 mr-1" />Hide Preview</>) : (<><EyeIcon className="h-3 w-3 mr-1" />Show Preview</>)}
                    </button>
                  </div>
                </div>
                {showPreview && previewContent && (
                  <div className='max-w-xl mx-auto'>
                    <RichTextEditor
                      value={previewContent}
                      onChange={() => { }}
                      readOnly
                      defaultPreviewMode
                      height="auto"
                      minHeight="0"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-400 transition-colors">Cancel</button>
                <button type="submit" disabled={saving || expertReadOnly} onClick={handleSubmit} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-50 transition-colors">
                  {saving ? 'Saving...' : editingHomepage ? 'Update Homepage' : 'Create Homepage'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white  shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4">
              <div className="flex items-center">
                <div className="flex-shrink-0"><TrashIcon className="h-6 w-6 text-red-600" /></div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Delete Homepage</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">Are you sure you want to delete "{deletingHomepage?.homepage_name}"? This action cannot be undone.</p>
                    {deletingIsExpertHomepage && (
                      <p className="mt-2 text-sm text-amber-700">
                        {isExpert
                          ? "This is the Domain Expert app's home screen. Users will see the welcome message and suggested questions instead."
                          : 'The Domain Expert app shows this homepage. Users will see the welcome message and suggested questions instead.'}
                      </p>
                    )}
                    {isExpert && deletingHomepage && isOnWebsite(deletingHomepage) && (
                      <p className="mt-2 text-sm text-amber-700">It's also shown on your website, so deleting it removes it there too.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
              <button type="button" onClick={handleDeleteCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50  focus:outline-none focus:ring-1 focus:ring-gray-500">Cancel</button>
              <button type="button" onClick={handleDeleteConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700  focus:outline-none focus:ring-1 focus:ring-red-500">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Card Preview Modal */}
      {previewModalHomepage && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setPreviewModalHomepage(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <EyeIcon className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900">Homepage Preview</h3>
                  <p className="text-sm text-gray-500 truncate">{previewModalHomepage.homepage_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!previewModalLoading && previewModalContent && (
                  <button type="button" onClick={copyPreviewModalContent} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    <ClipboardIcon className="h-3 w-3 mr-1" />Copy
                  </button>
                )}
                <button type="button" onClick={() => setPreviewModalHomepage(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Close">
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body — preview canvas */}
            <div className="flex-1 overflow-y-auto bg-gray-50 px-4 sm:px-6 py-6">
              {previewModalLoading ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-indigo-600 mb-3"></div>
                  <p className="text-sm">Generating preview…</p>
                </div>
              ) : previewModalContent ? (
                <>
                {isExpert && <p className="mb-3 text-center text-xs text-gray-500">Forms and CTAs are not shown in the Domain Expert app.</p>}
                <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <RichTextEditor
                    value={previewModalContent}
                    onChange={() => { }}
                    readOnly
                    defaultPreviewMode
                    height="auto"
                    minHeight="0"
                  />
                </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <EyeOffIcon className="mx-auto h-8 w-8 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">No preview content available.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex justify-end">
              <button type="button" onClick={() => setPreviewModalHomepage(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-400 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
